import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/api";

/**
 * Tavolata by IntegraNube
 * src/Pages/Caja.js
 *
 * Contratos reales utilizados:
 *   GET /restaurante/pedidos?estado=CUENTA_SOLICITADA,PARCIALMENTE_PAGADO
 *   GET /restaurante/pedidos/:pedidoId
 *
 * Esta vista recibe las mesas que Mesero dejó en CUENTA_SOLICITADA.
 *
 * IMPORTANTE:
 * El backend Restaurant disponible todavía no expone un endpoint real de
 * registro/cierre de pago. Por eso esta vista NO simula un cobro exitoso.
 * Deja la cuenta seleccionada y lista para conectar al flujo definitivo
 * de pago (ventas_pos / restaurante_caja) sin inventar contratos.
 */

const REFRESH_MS = 8000;

function first(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function money(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(num(value, 0));
}

function errorMessage(error, fallback = "No fue posible completar la operación.") {
  return (
    error?.friendlyMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function rowsFrom(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  if (Array.isArray(payload?.pedidos)) return payload.pedidos;
  return [];
}

function itemName(item) {
  return String(
    first(
      item?.nombre_snapshot,
      item?.producto_nombre,
      item?.nombre,
      "Producto"
    )
  );
}

function itemQty(item) {
  return Math.max(0, num(first(item?.cantidad, item?.quantity), 0));
}

function itemTotal(item) {
  const explicit = first(item?.total, item?.total_bruto);
  if (explicit !== null) return num(explicit, 0);

  return (
    itemQty(item) *
    num(first(item?.precio_unitario, item?.precio, item?.price), 0)
  );
}

function elapsedMinutes(value) {
  if (!value) return 0;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function orderLabel(order) {
  if (order?.mesa_nombre) return order.mesa_nombre;
  if (order?.tipo === "MESA" && order?.mesa_id) return `Mesa ${order.mesa_id}`;
  return order?.tipo === "PARA_LLEVAR"
    ? "Para llevar"
    : order?.tipo === "MOSTRADOR"
      ? "Mostrador"
      : order?.tipo || "Pedido";
}

function navigate(route) {
  window.history.pushState({}, "", route);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export default function Caja() {
  const mountedRef = useRef(true);
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const pedidoInicial = query.get("pedidoId") || query.get("pedido_id") || "";

  const [cuentas, setCuentas] = useState([]);
  const [selectedId, setSelectedId] = useState(pedidoInicial);
  const [pedido, setPedido] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => {
      if (mountedRef.current) setNotice(null);
    }, 4200);
  }, []);

  const loadAccounts = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);

    try {
      const response = await api.get("/restaurante/pedidos", {
        params: {
          estado: "CUENTA_SOLICITADA,PARCIALMENTE_PAGADO",
        },
      });

      const rows = rowsFrom(response.data)
        .filter((row) =>
          ["CUENTA_SOLICITADA", "PARCIALMENTE_PAGADO"].includes(
            String(row?.estado || "").toUpperCase()
          )
        )
        .sort((a, b) => {
          const ta = new Date(
            first(a?.cuenta_solicitada_at, a?.updated_at, a?.abierto_at, 0)
          ).getTime();
          const tb = new Date(
            first(b?.cuenta_solicitada_at, b?.updated_at, b?.abierto_at, 0)
          ).getTime();

          return ta - tb;
        });

      if (!mountedRef.current) return;

      setCuentas(rows);
      setLastUpdate(new Date());

      setSelectedId((current) => {
        if (current && rows.some((row) => String(row.id) === String(current))) {
          return current;
        }
        return rows[0]?.id ? String(rows[0].id) : "";
      });

      window.dispatchEvent(
        new CustomEvent("tavolata:indicators", {
          detail: {
            caja: {
              count: rows.length,
              label: rows.length
                ? `${rows.length} cuenta${rows.length === 1 ? "" : "s"} pendiente${rows.length === 1 ? "" : "s"}`
                : "Sin cuentas pendientes",
              tone: rows.length ? "danger" : "neutral",
            },
          },
        })
      );
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible cargar las cuentas pendientes.")
      );
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [showNotice]);

  const loadDetail = useCallback(async (pedidoId) => {
    if (!pedidoId) {
      setPedido(null);
      return;
    }

    setLoadingDetail(true);

    try {
      const response = await api.get(`/restaurante/pedidos/${pedidoId}`);
      if (!mountedRef.current) return;
      setPedido(response.data || null);
    } catch (error) {
      showNotice(
        "error",
        errorMessage(error, "No fue posible cargar el detalle de la cuenta.")
      );
      if (mountedRef.current) setPedido(null);
    } finally {
      if (mountedRef.current) setLoadingDetail(false);
    }
  }, [showNotice]);

  useEffect(() => {
    mountedRef.current = true;
    loadAccounts();

    return () => {
      mountedRef.current = false;
      window.clearTimeout(showNotice._timer);
    };
  }, [loadAccounts, showNotice]);

  useEffect(() => {
    if (!selectedId) {
      setPedido(null);
      return;
    }

    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadAccounts({ silent: true });
      }
    }, REFRESH_MS);

    return () => window.clearInterval(timer);
  }, [loadAccounts]);

  const cuentasVisibles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cuentas;

    return cuentas.filter((row) => {
      const values = [
        row?.numero,
        row?.mesa_nombre,
        row?.mesa_codigo,
        row?.zona_nombre,
        row?.tipo,
        row?.id,
      ];

      return values.some((value) =>
        String(value || "").toLowerCase().includes(term)
      );
    });
  }, [cuentas, search]);

  const totalPendiente = useMemo(
    () => cuentas.reduce((sum, row) => sum + num(row?.total, 0), 0),
    [cuentas]
  );

  const pedidoItems = useMemo(
    () => (Array.isArray(pedido?.items) ? pedido.items : []),
    [pedido]
  );

  const itemsValidos = useMemo(
    () =>
      pedidoItems.filter(
        (item) => String(item?.estado || "").toUpperCase() !== "CANCELADO"
      ),
    [pedidoItems]
  );

  const subtotalDetalle = useMemo(
    () => itemsValidos.reduce((sum, item) => sum + itemTotal(item), 0),
    [itemsValidos]
  );

  const pendingMinutes = pedido
    ? elapsedMinutes(
        first(
          pedido?.cuenta_solicitada_at,
          pedido?.updated_at,
          pedido?.abierto_at
        )
      )
    : 0;

  function openMesero() {
    if (!pedido) return;

    const qs = new URLSearchParams();
    if (pedido.mesa_id) qs.set("mesaId", pedido.mesa_id);
    if (pedido.id) qs.set("pedidoId", pedido.id);

    navigate(`/mesero?${qs.toString()}`);
  }

  function selectAccount(id) {
    setSelectedId(String(id));

    try {
      const url = new URL(window.location.href);
      url.searchParams.set("pedidoId", String(id));
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    } catch (_) {
      // no-op
    }
  }

  function explainPayment() {
    showNotice(
      "info",
      "La cuenta ya está lista. Falta conectar el endpoint definitivo de pago/cierre para no simular un cobro."
    );
  }

  return (
    <div className="cash-shell">
      <style>{`
        * { box-sizing: border-box; }

        :root {
          --cash-bg: #f5f7f6;
          --cash-paper: #ffffff;
          --cash-text: #17211c;
          --cash-muted: #6b756f;
          --cash-line: #e3e8e5;
          --cash-brand: #b86d2f;
          --cash-brand-dark: #8d4d1d;
          --cash-brand-soft: #fff2e7;
          --cash-purple: #7442a4;
          --cash-purple-soft: #f5edfc;
          --cash-green: #168a52;
          --cash-red: #c43d43;
          --cash-shadow: 0 14px 34px rgba(27, 43, 34, .08);
        }

        .cash-shell {
          min-height: 100vh;
          min-height: 100dvh;
          background: var(--cash-bg);
          color: var(--cash-text);
          font-family:
            Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont,
            "Segoe UI", sans-serif;
        }

        button, input { font: inherit; }

        .cash-page {
          width: min(1480px, 100%);
          margin: 0 auto;
          padding: 18px;
        }

        .cash-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 14px;
        }

        .cash-head h1 {
          margin: 0;
          font-size: clamp(24px, 3vw, 32px);
          letter-spacing: -.035em;
        }

        .cash-head p {
          margin: 5px 0 0;
          color: var(--cash-muted);
          font-size: 13px;
        }

        .cash-refresh {
          min-height: 44px;
          padding: 0 14px;
          border: 1px solid var(--cash-line);
          border-radius: 12px;
          background: #fff;
          color: #47534c;
          font-weight: 850;
          cursor: pointer;
        }

        .cash-metrics {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 12px;
        }

        .cash-metric {
          padding: 12px 14px;
          border: 1px solid var(--cash-line);
          border-radius: 14px;
          background: #fff;
        }

        .cash-metric small {
          display: block;
          color: var(--cash-muted);
          font-size: 11px;
          font-weight: 750;
        }

        .cash-metric strong {
          display: block;
          margin-top: 3px;
          font-size: 22px;
          line-height: 1.05;
        }

        .cash-layout {
          display: grid;
          grid-template-columns: minmax(300px, 410px) minmax(0, 1fr);
          gap: 14px;
          align-items: start;
        }

        .cash-panel {
          overflow: hidden;
          border: 1px solid var(--cash-line);
          border-radius: 17px;
          background: var(--cash-paper);
          box-shadow: var(--cash-shadow);
        }

        .cash-list-head {
          padding: 12px;
          border-bottom: 1px solid var(--cash-line);
        }

        .cash-search {
          width: 100%;
          min-height: 44px;
          padding: 0 13px;
          border: 1px solid #dce3df;
          border-radius: 11px;
          outline: none;
          background: #fafcfb;
        }

        .cash-search:focus {
          border-color: #d1a67f;
          box-shadow: 0 0 0 3px rgba(184,109,47,.08);
        }

        .cash-list {
          max-height: calc(100dvh - 315px);
          overflow-y: auto;
          padding: 8px;
        }

        .cash-account {
          width: 100%;
          margin-bottom: 7px;
          padding: 12px;
          border: 1px solid #e1e7e3;
          border-radius: 13px;
          background: #fff;
          text-align: left;
          cursor: pointer;
        }

        .cash-account.active {
          border-color: #b797d2;
          background: var(--cash-purple-soft);
          box-shadow: 0 0 0 2px rgba(116,66,164,.06);
        }

        .cash-account-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
        }

        .cash-account-name strong {
          display: block;
          font-size: 16px;
        }

        .cash-account-name small {
          display: block;
          margin-top: 3px;
          color: var(--cash-muted);
          font-size: 11px;
        }

        .cash-account-total {
          color: #5d2d88;
          font-size: 17px;
          font-weight: 950;
          white-space: nowrap;
        }

        .cash-account-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 9px;
        }

        .cash-chip {
          min-height: 25px;
          display: inline-flex;
          align-items: center;
          padding: 0 8px;
          border-radius: 999px;
          background: #f1f4f2;
          color: #647067;
          font-size: 10px;
          font-weight: 800;
        }

        .cash-chip.waiting {
          background: var(--cash-purple-soft);
          color: #6d399d;
        }

        .cash-detail {
          min-height: 520px;
        }

        .cash-detail-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          padding: 17px;
          border-bottom: 1px solid var(--cash-line);
        }

        .cash-detail-head h2 {
          margin: 0;
          font-size: 23px;
        }

        .cash-detail-head p {
          margin: 5px 0 0;
          color: var(--cash-muted);
          font-size: 12px;
        }

        .cash-status {
          min-height: 31px;
          display: inline-flex;
          align-items: center;
          padding: 0 10px;
          border-radius: 999px;
          background: var(--cash-purple-soft);
          color: #6d399d;
          font-size: 11px;
          font-weight: 900;
          white-space: nowrap;
        }

        .cash-detail-body {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 300px;
          min-height: 440px;
        }

        .cash-items {
          padding: 14px;
          border-right: 1px solid var(--cash-line);
        }

        .cash-section-title {
          margin: 0 0 10px;
          color: var(--cash-muted);
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .05em;
        }

        .cash-item {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          padding: 11px 4px;
          border-bottom: 1px dashed #e3e8e5;
        }

        .cash-item:last-child { border-bottom: 0; }

        .cash-item-name {
          font-size: 13px;
          font-weight: 850;
          line-height: 1.3;
        }

        .cash-item-note {
          margin-top: 3px;
          color: var(--cash-muted);
          font-size: 10px;
          line-height: 1.35;
        }

        .cash-item-total {
          font-size: 13px;
          font-weight: 900;
          white-space: nowrap;
        }

        .cash-summary {
          padding: 14px;
          background: #fbfcfb;
        }

        .cash-info-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .cash-info {
          padding: 10px;
          border-radius: 11px;
          background: #fff;
          border: 1px solid #e8ece9;
        }

        .cash-info small {
          display: block;
          color: var(--cash-muted);
          font-size: 10px;
        }

        .cash-info strong {
          display: block;
          margin-top: 3px;
          font-size: 13px;
        }

        .cash-total {
          margin-top: 12px;
          padding: 14px;
          border-radius: 13px;
          background: #17211c;
          color: #fff;
        }

        .cash-total small {
          display: block;
          opacity: .68;
          font-size: 11px;
        }

        .cash-total strong {
          display: block;
          margin-top: 4px;
          font-size: 29px;
          line-height: 1;
        }

        .cash-ready {
          margin-top: 10px;
          padding: 11px;
          border: 1px solid #c9afe1;
          border-radius: 12px;
          background: var(--cash-purple-soft);
          color: #62348d;
          font-size: 11px;
          line-height: 1.45;
        }

        .cash-action {
          width: 100%;
          min-height: 47px;
          margin-top: 9px;
          border: 0;
          border-radius: 11px;
          font-weight: 900;
          cursor: pointer;
        }

        .cash-action.primary {
          background: var(--cash-brand);
          color: #fff;
        }

        .cash-action.secondary {
          border: 1px solid #dce3df;
          background: #fff;
          color: #46524b;
        }

        .cash-empty,
        .cash-loading {
          min-height: 360px;
          display: grid;
          place-items: center;
          padding: 24px;
          color: var(--cash-muted);
          text-align: center;
        }

        .cash-notice {
          position: fixed;
          left: 50%;
          bottom: 22px;
          z-index: 2100;
          transform: translateX(-50%);
          max-width: min(560px, calc(100vw - 28px));
          padding: 11px 14px;
          border-radius: 12px;
          box-shadow: 0 14px 38px rgba(15,23,42,.18);
          background: #17211c;
          color: #fff;
          font-size: 12px;
          font-weight: 750;
        }

        .cash-notice.error { background: #a92e34; }
        .cash-notice.success { background: #176b45; }
        .cash-notice.info { background: #4f3b70; }

        @media (max-width: 900px) {
          .cash-layout {
            grid-template-columns: 1fr;
          }

          .cash-list {
            max-height: 330px;
          }

          .cash-detail-body {
            grid-template-columns: 1fr;
          }

          .cash-items {
            border-right: 0;
            border-bottom: 1px solid var(--cash-line);
          }
        }

        @media (max-width: 560px) {
          .cash-page {
            padding: 10px;
          }

          .cash-metrics {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .cash-metrics .cash-metric:last-child {
            grid-column: 1 / -1;
          }

          .cash-head {
            align-items: center;
          }

          .cash-detail-head {
            display: block;
          }

          .cash-status {
            margin-top: 9px;
          }
        }
      `}</style>

      <main className="cash-page">
        <header className="cash-head">
          <div>
            <h1>Caja</h1>
            <p>
              Cuentas solicitadas por Mesero · actualización automática cada 8 segundos
            </p>
          </div>

          <button
            type="button"
            className="cash-refresh"
            onClick={() => loadAccounts({ silent: true })}
            disabled={refreshing}
          >
            {refreshing ? "Actualizando…" : "Actualizar"}
          </button>
        </header>

        <div className="cash-metrics">
          <div className="cash-metric">
            <small>Cuentas pendientes</small>
            <strong>{cuentas.length}</strong>
          </div>

          <div className="cash-metric">
            <small>Total por cobrar</small>
            <strong>{money(totalPendiente)}</strong>
          </div>

          <div className="cash-metric">
            <small>Última actualización</small>
            <strong style={{ fontSize: 16 }}>
              {lastUpdate ? formatTime(lastUpdate) : "—"}
            </strong>
          </div>
        </div>

        <div className="cash-layout">
          <section className="cash-panel">
            <div className="cash-list-head">
              <input
                className="cash-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar mesa o pedido…"
                autoComplete="off"
              />
            </div>

            <div className="cash-list">
              {loading ? (
                <div className="cash-loading">Cargando cuentas…</div>
              ) : cuentasVisibles.length ? (
                cuentasVisibles.map((row) => {
                  const active = String(row.id) === String(selectedId);
                  const mins = elapsedMinutes(
                    first(
                      row?.cuenta_solicitada_at,
                      row?.updated_at,
                      row?.abierto_at
                    )
                  );

                  return (
                    <button
                      type="button"
                      key={row.id}
                      className={`cash-account ${active ? "active" : ""}`}
                      onClick={() => selectAccount(row.id)}
                    >
                      <div className="cash-account-top">
                        <div className="cash-account-name">
                          <strong>{orderLabel(row)}</strong>
                          <small>
                            Pedido #{row.numero || row.id}
                            {row.zona_nombre ? ` · ${row.zona_nombre}` : ""}
                          </small>
                        </div>

                        <div className="cash-account-total">
                          {money(row.total)}
                        </div>
                      </div>

                      <div className="cash-account-meta">
                        <span className="cash-chip waiting">
                          {String(row.estado || "").replaceAll("_", " ")}
                        </span>
                        <span className="cash-chip">{mins} min esperando</span>
                        <span className="cash-chip">
                          {row.comensales || 1} persona{num(row.comensales, 1) === 1 ? "" : "s"}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="cash-empty">
                  <div>
                    <strong>No hay cuentas pendientes.</strong>
                    <div style={{ marginTop: 6 }}>
                      Cuando Mesero solicite una cuenta aparecerá aquí.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="cash-panel cash-detail">
            {!selectedId ? (
              <div className="cash-empty">
                <div>
                  <strong>Selecciona una cuenta</strong>
                  <div style={{ marginTop: 6 }}>
                    Aquí verás el detalle completo del pedido.
                  </div>
                </div>
              </div>
            ) : loadingDetail ? (
              <div className="cash-loading">Cargando detalle…</div>
            ) : pedido ? (
              <>
                <div className="cash-detail-head">
                  <div>
                    <h2>{orderLabel(pedido)}</h2>
                    <p>
                      Pedido #{pedido.numero || pedido.id}
                      {pedido.zona_nombre ? ` · ${pedido.zona_nombre}` : ""}
                    </p>
                  </div>

                  <span className="cash-status">
                    {String(pedido.estado || "").replaceAll("_", " ")}
                  </span>
                </div>

                <div className="cash-detail-body">
                  <div className="cash-items">
                    <h3 className="cash-section-title">Detalle de consumo</h3>

                    {itemsValidos.length ? (
                      itemsValidos.map((item, index) => (
                        <div className="cash-item" key={item.id || index}>
                          <div>
                            <div className="cash-item-name">
                              {itemQty(item)} × {itemName(item)}
                            </div>

                            <div className="cash-item-note">
                              {String(item.estado || "").replaceAll("_", " ")}
                              {item.observacion ? ` · ${item.observacion}` : ""}
                            </div>

                            {Array.isArray(item.modificadores) &&
                              item.modificadores.length > 0 && (
                                <div className="cash-item-note">
                                  {item.modificadores
                                    .map(
                                      (mod) =>
                                        `${mod.opcion_nombre_snapshot || mod.opcion_nombre || "Opción"}`
                                    )
                                    .join(" · ")}
                                </div>
                              )}
                          </div>

                          <div className="cash-item-total">
                            {money(itemTotal(item))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="cash-empty" style={{ minHeight: 220 }}>
                        Pedido sin productos cobrables.
                      </div>
                    )}
                  </div>

                  <aside className="cash-summary">
                    <h3 className="cash-section-title">Cuenta</h3>

                    <div className="cash-info-grid">
                      <div className="cash-info">
                        <small>Personas</small>
                        <strong>{pedido.comensales || 1}</strong>
                      </div>
                      <div className="cash-info">
                        <small>Esperando</small>
                        <strong>{pendingMinutes} min</strong>
                      </div>
                      <div className="cash-info">
                        <small>Solicitada</small>
                        <strong>
                          {formatTime(
                            first(
                              pedido.cuenta_solicitada_at,
                              pedido.updated_at
                            )
                          )}
                        </strong>
                      </div>
                      <div className="cash-info">
                        <small>Ítems</small>
                        <strong>{itemsValidos.length}</strong>
                      </div>
                    </div>

                    <div className="cash-total">
                      <small>Total de la cuenta</small>
                      <strong>{money(first(pedido.total, subtotalDetalle))}</strong>
                    </div>

                    <div className="cash-ready">
                      <strong>✓ Cuenta lista para cobro.</strong>
                      <br />
                      Mesero ya cerró la toma del pedido. La mesa sigue ocupada
                      hasta que el pago definitivo cierre el pedido.
                    </div>

                    <button
                      type="button"
                      className="cash-action primary"
                      onClick={explainPayment}
                    >
                      Cobrar
                    </button>

                    <button
                      type="button"
                      className="cash-action secondary"
                      onClick={openMesero}
                    >
                      Volver al pedido
                    </button>
                  </aside>
                </div>
              </>
            ) : (
              <div className="cash-empty">
                No se pudo obtener el pedido seleccionado.
              </div>
            )}
          </section>
        </div>
      </main>

      {notice && (
        <div className={`cash-notice ${notice.type}`}>
          {notice.message}
        </div>
      )}
    </div>
  );
}
